
import { Session, User } from '@supabase/supabase-js';
import { PERMISSIONS_CONFIG } from './config/permissionsConfig';
import { usePermissionsState } from './hooks/usePermissionsState';
import { usePermissionsRefs } from './hooks/usePermissionsRefs';
import { usePermissionsRefresh } from './hooks/usePermissionsRefresh';
import { useDebouncedRefresh } from './hooks/useDebouncedRefresh';
import { usePermissionsEvents } from './hooks/usePermissionsEvents';
import { useSubscriptionManagement } from './hooks/useSubscriptionManagement';

/**
 * Main coordinator hook for permissions logic
 * Orchestrates all permission-related operations
 */
export const usePermissionsLogic = (
  session: Session | null,
  user: User | null,
  authReady: boolean
) => {
  // State management
  const {
    permissions,
    setPermissions,
    isRefreshing,
    setIsRefreshing,
    error,
    setError,
    subscriptionSource,
    setSubscriptionSource,
  } = usePermissionsState();

  // Refs management
  const {
    lastRefreshedRef,
    isRefreshingRef,
    debounceTimeoutRef,
    eventListenerCleanupRef,
    hasInitiallyRefreshedRef,
  } = usePermissionsRefs();

  // Refresh operations
  const { refreshPermissions, forceRefreshPermissions } = usePermissionsRefresh({
    authReady,
    userId: user?.id,
    session,
    setPermissions,
    setSubscriptionSource,
    setIsRefreshing,
    setError,
    isRefreshingRef,
    lastRefreshedRef,
    hasInitiallyRefreshedRef,
  });

  // Debounced refresh
  const { debouncedRefreshPermissions } = useDebouncedRefresh(
    refreshPermissions,
    debounceTimeoutRef
  );

  // Event handling
  usePermissionsEvents(
    authReady,
    user?.id,
    refreshPermissions,
    eventListenerCleanupRef
  );

  // Subscription management
  const { upgradeInProgress, initiateSubscription } = useSubscriptionManagement(
    user?.id,
    session,
    authReady
  );

  return {
    permissions,
    setPermissions,
    isRefreshing,
    error,
    subscriptionSource,
    upgradeInProgress,
    refreshPermissions,
    forceRefreshPermissions,
    initiateSubscription,
    debouncedRefreshPermissions,
    lastRefreshedRef,
    debounceTimeoutRef,
    hasInitiallyRefreshedRef,
    TTL: PERMISSIONS_CONFIG.TTL,
  };
};
